"use client";

import { useEffect, useMemo, useRef } from "react";
import { useIdentity } from "@/contexts/IdentityContext";
import { resolveVipStatus } from "@/lib/vip/status";
import { type VipInfo, VipLevel } from "@/types/vip";
import { useBabyOdinBalance } from "./useBabyOdinBalance";
import { useLanguage } from "./useLanguage";
import { useVipWhitelist } from "./useVipWhitelist";

export interface VipStatusData {
	vipInfo: VipInfo;
	isVip: boolean;
	isLoading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
}

export interface UseVipStatusOptions {
	identityId?: string;
	enabled?: boolean;
}

export function useVipStatus(
	options: UseVipStatusOptions = {},
): VipStatusData {
	const { principal, isAuthenticated } = useIdentity();
	const { language } = useLanguage();
	const targetIdentityId = options.identityId ?? principal ?? undefined;
	const enabled = options.enabled ?? !!(options.identityId || isAuthenticated);

	const {
		lpUsdValue,
		isLoading: babyOdinLoading,
		error: babyOdinError,
		refresh: refreshBabyOdin,
	} = useBabyOdinBalance({
		identityId: options.identityId,
		enabled,
	});

	const {
		whitelistInfo,
		isLoading: whitelistLoading,
		error: whitelistError,
		refresh: refreshWhitelist,
	} = useVipWhitelist({
		identityId: options.identityId,
		enabled,
	});
	const previousLevelRef = useRef<VipLevel>(VipLevel.NONE);

	const vipInfo = useMemo((): VipInfo => {
		return resolveVipStatus({
			language,
			subjectId: targetIdentityId,
			isAuthenticated,
			lpUsdValue,
			previousLevel: previousLevelRef.current,
			whitelistInfo,
		}).vipInfo;
	}, [
		targetIdentityId,
		isAuthenticated,
		lpUsdValue,
		whitelistInfo,
		language,
	]);

	useEffect(() => {
		previousLevelRef.current = vipInfo.level;
	}, [vipInfo.level]);

	const isVip = vipInfo.level !== VipLevel.NONE;
	const isLoading = enabled ? babyOdinLoading || whitelistLoading : false;
	const error = babyOdinError || whitelistError;
	const refresh = async () => {
		await Promise.all([refreshBabyOdin(), refreshWhitelist()]);
	};

	return {
		vipInfo,
		isVip,
		isLoading,
		error,
		refresh,
	};
}

export default useVipStatus;
